// src/pages/Favorites.jsx
import React, { useContext, useMemo, useReducer, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Heart, SearchX, Pencil, Trash2, Plus, FolderMinus } from "lucide-react";
import { ThemeContext } from "../App";
import Modal from "../components/Modal";
import Select from "../components/select/Select";
import { EmptyState, StarRating } from "../components/common/Common";
import { fetchRecipeDetails } from "../api/recipes";
import { useFavorites } from "../utils/favorites";
import { useCollections, useRecipeNotes } from "../utils/stores";

import "./Favorites.css";
import Swal from "sweetalert2";

/* ---------- Reducer ---------- */
const reducer = (state, action) => {
  switch (action.type) {
    case "FILTER_TEXT":
      return { ...state, filterText: action.payload };
    case "FILTER_TAGS":
      return { ...state, selectedTags: action.payload };
    case "CLEAR_FILTERS":
      return { ...state, filterText: "", selectedTags: [] };
    case "TOGGLE_SELECT": {
      const id = action.payload;
      return {
        ...state,
        selectedIds: state.selectedIds.includes(id)
          ? state.selectedIds.filter((x) => x !== id)
          : [...state.selectedIds, id],
      };
    }
    case "SELECT_ALL":
      // `action.payload` = boolean (true = select all filtered)
      return {
        ...state,
        selectedIds: action.payload ? action.filteredIds : [],
      };
    case "CLEAR_SELECTION":
      return { ...state, selectedIds: [] };
    default:
      return state;
  }
};

const initialState = {
  filterText: "",
  selectedTags: [],
  selectedIds: [],
};

const toast = (icon, title) =>
  Swal.fire({ toast: true, position: "bottom", icon, title, showConfirmButton: false, timer: 1600 });

const askName = (title, value = "") =>
  Swal.fire({
    title,
    input: "text",
    inputValue: value,
    inputPlaceholder: "e.g. Weeknight dinners",
    showCancelButton: true,
    confirmButtonText: "Save",
    inputValidator: (v) => (!v.trim() ? "Please enter a name" : undefined),
  }).then((r) => (r.isConfirmed ? r.value.trim() : null));

/* ---------- Sortable card ---------- */
const SortableCard = ({ item, selected, rating, onToggle, onView, onRemove }) => {
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 5 : undefined,
  };

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`fav-card ${isDragging ? "dragging" : ""} ${selected ? "selected" : ""}`}
    >
      <label className="card-select">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select ${item.title}`}
        />
      </label>

      <button
        ref={setActivatorNodeRef}
        className="card-handle"
        aria-label={`Reorder ${item.title}`}
        title="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} />
      </button>

      <img
        className="card-image"
        src={item.image}
        alt={item.title}
        loading="lazy"
        onClick={onView}
        style={{ cursor: "pointer" }}
      />
      <div className="card-body">
        <h3 className="card-title">{item.title}</h3>
        {rating > 0 && (
          <div className="card-rating">
            <StarRating value={rating} size={15} />
          </div>
        )}

        <div className="card-actions">
          <button className="btn btn-remove" onClick={onRemove}>
            Remove
          </button>
          <button className="btn btn-view" onClick={onView}>
            View
          </button>
        </div>
      </div>
    </article>
  );
};

/* ---------- Main Component ---------- */
const Favorites = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [activeCollection, setActiveCollection] = useState("all");
  const [isModalOpen, setModalOpen] = useState(false);
  const [recipeData, setRecipeData] = useState(null);
  const { theme } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();

  const { favorites, removeFavorite, removeMany, replaceAll } = useFavorites();
  const cols = useCollections();
  const { get: getNote } = useRecipeNotes();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  /* ---------- URL share handling ---------- */
  const sharedIds = useMemo(() => {
    const ids = new URLSearchParams(location.search).get("ids");
    return ids ? ids.split(",") : null;
  }, [location.search]);

  const currentCollection = cols.collections.find((c) => c.id === activeCollection);

  /* ---------- Filtering ---------- */
  const filtered = favorites
    .filter((r) => !sharedIds || sharedIds.includes(String(r.id)))
    .filter((r) => !currentCollection || currentCollection.ids.includes(r.id))
    .filter((r) => r.title?.toLowerCase().includes(state.filterText.toLowerCase()))
    .filter((r) => {
      if (state.selectedTags.length === 0) return true;
      const tags = [
        ...(r.cuisines || []),
        ...(r.dishTypes || []),
        ...(r.diets || []),
        ...(r.strCategory ? [r.strCategory] : []),
        ...(r.strArea ? [r.strArea] : []),
      ].map((t) => t.toLowerCase());
      return state.selectedTags.some((st) => tags.includes(st.toLowerCase()));
    });

  const filteredIds = filtered.map((r) => r.id);
  const hasFilters = !!(state.filterText || state.selectedTags.length);

  /* ---------- Collections ---------- */
  const createCollection = async (withIds = []) => {
    const name = await askName("New collection");
    if (!name) return;
    const id = cols.create(name, withIds);
    setActiveCollection(id);
    dispatch({ type: "CLEAR_SELECTION" });
    toast("success", `Collection “${name}” created`);
  };

  const renameCollection = async () => {
    const name = await askName("Rename collection", currentCollection.name);
    if (name) cols.rename(currentCollection.id, name);
  };

  const deleteCollection = async () => {
    const r = await Swal.fire({
      title: `Delete “${currentCollection.name}”?`,
      text: "Your recipes stay in Favorites — only the collection is removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (r.isConfirmed) {
      cols.remove(currentCollection.id);
      setActiveCollection("all");
    }
  };

  const addSelectedToCollection = (value) => {
    if (!value) return;
    if (value === "__new") {
      createCollection(state.selectedIds);
      return;
    }
    cols.addIds(value, state.selectedIds);
    const name = cols.collections.find((c) => c.id === value)?.name;
    toast("success", `Added ${state.selectedIds.length} to “${name}”`);
    dispatch({ type: "CLEAR_SELECTION" });
  };

  /* ---------- Bulk actions ---------- */
  const removeSelected = () => {
    const count = state.selectedIds.length;
    removeMany(state.selectedIds);
    dispatch({ type: "CLEAR_SELECTION" });
    Swal.fire({
      icon: "success",
      title: "Removed!",
      text: `${count} recipe(s) have been removed from your favorites.`,
      showConfirmButton: false,
      timer: 1500,
    });
  };

  const exportSelected = () => {
    const data = favorites.filter((r) => state.selectedIds.includes(r.id));
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "my-favorites.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const shareList = () => {
    const ids = state.selectedIds.length ? state.selectedIds : favorites.map((r) => r.id);
    const url = `${window.location.origin}${window.location.pathname}?ids=${ids.join(",")}`;
    navigator.clipboard.writeText(url).then(() =>
      Swal.fire({
        icon: "success",
        title: "Copied!",
        text: "The link has been copied to your clipboard.",
        showConfirmButton: false,
        timer: 1500,
      })
    );
  };

  const viewRecipe = async (item) => {
    try {
      setRecipeData(await fetchRecipeDetails(item));
      setModalOpen(true);
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load recipe.",
      });
    }
  };

  const removeOne = (item) => {
    removeFavorite(item.id);
    toast("success", "Removed from your favorites");
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = favorites.findIndex((f) => f.id === active.id);
    const newIndex = favorites.findIndex((f) => f.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    replaceAll(arrayMove(favorites, oldIndex, newIndex));
  };

  const collectionOptions = [
    { value: "", label: "Add to collection…" },
    ...cols.collections.map((c) => ({ value: c.id, label: c.name })),
    { value: "__new", label: "+ New collection…" },
  ];

  /* ---------- Render ---------- */
  return (
    <>
      <main className={`favorites-container ${theme ? "dark" : ""}`}>
        {favorites.length > 0 ? (
          <div className="favorites-wrapper">
            <header className="favorites-header">
              <span className="favorites-eyebrow">
                {favorites.length} saved {favorites.length === 1 ? "recipe" : "recipes"}
              </span>
              <h1 className="favorites-title">My Favorites</h1>
            </header>

            {sharedIds && (
              <div className="shared-banner" role="status">
                Showing a shared selection.
                <button onClick={() => navigate("/favorites", { replace: true })}>Show all my favorites</button>
              </div>
            )}

            {/* ---- Collections ---- */}
            <div className="collections" role="tablist" aria-label="Collections">
              <button
                role="tab"
                aria-selected={activeCollection === "all"}
                className={`collection-chip ${activeCollection === "all" ? "active" : ""}`}
                onClick={() => setActiveCollection("all")}
              >
                All <span>{favorites.length}</span>
              </button>
              {cols.collections.map((c) => (
                <button
                  key={c.id}
                  role="tab"
                  aria-selected={activeCollection === c.id}
                  className={`collection-chip ${activeCollection === c.id ? "active" : ""}`}
                  onClick={() => setActiveCollection(c.id)}
                >
                  {c.name} <span>{c.ids.filter((id) => favorites.some((f) => f.id === id)).length}</span>
                </button>
              ))}
              <button className="collection-chip add" onClick={() => createCollection()}>
                <Plus size={14} /> New collection
              </button>
            </div>
            {currentCollection && (
              <div className="collection-tools">
                <button onClick={renameCollection}><Pencil size={14} /> Rename</button>
                <button className="danger" onClick={deleteCollection}><Trash2 size={14} /> Delete collection</button>
              </div>
            )}

            {/* ---- Controls ---- */}
            <div className="controls">
              <input
                className="search-input"
                type="search"
                aria-label="Search favorites"
                placeholder="Search favorites..."
                value={state.filterText}
                onChange={(e) =>
                  dispatch({ type: "FILTER_TEXT", payload: e.target.value })
                }
              />

              <TagFilter
                favorites={favorites}
                selected={state.selectedTags}
                onChange={(tags) =>
                  dispatch({ type: "FILTER_TAGS", payload: tags })
                }
              />

              <div className="bulk-actions">
                <label className="select-all-label">
                  <input
                    type="checkbox"
                    checked={
                      filteredIds.length > 0 &&
                      state.selectedIds.length === filteredIds.length
                    }
                    onChange={(e) =>
                      dispatch({
                        type: "SELECT_ALL",
                        payload: e.target.checked,
                        filteredIds,
                      })
                    }
                  />{" "}
                  Select all
                </label>

                {state.selectedIds.length > 0 && (
                  <>
                    <button className="btn btn-sm btn-danger" onClick={removeSelected}>
                      Remove ({state.selectedIds.length})
                    </button>
                    <button className="btn btn-sm btn-secondary" onClick={exportSelected}>
                      Export
                    </button>
                    <button className="btn btn-sm btn-primary" onClick={shareList}>
                      Share
                    </button>
                    <Select
                      className="bulk-select"
                      ariaLabel="Add selected recipes to a collection"
                      value=""
                      onChange={addSelectedToCollection}
                      options={collectionOptions}
                    />
                    {currentCollection && (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          cols.removeIds(currentCollection.id, state.selectedIds);
                          dispatch({ type: "CLEAR_SELECTION" });
                        }}
                      >
                        <FolderMinus size={14} /> Remove from collection
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ---- Drag & Drop Grid ---- */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={filteredIds} strategy={rectSortingStrategy}>
                <div className="favorites-grid">
                  {filtered.map((item) => (
                    <SortableCard
                      key={item.id}
                      item={item}
                      rating={getNote(item.id).rating}
                      selected={state.selectedIds.includes(item.id)}
                      onToggle={() => dispatch({ type: "TOGGLE_SELECT", payload: item.id })}
                      onView={() => viewRecipe(item)}
                      onRemove={() => removeOne(item)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {filtered.length === 0 && (
              <EmptyState
                compact
                icon={SearchX}
                title={currentCollection && !hasFilters ? "This collection is empty" : "No favorites match your filters"}
                text={
                  currentCollection && !hasFilters
                    ? "Select recipes in All, then use “Add to collection”."
                    : "Try a different search or clear the filters."
                }
                action={
                  hasFilters
                    ? { label: "Clear filters", onClick: () => dispatch({ type: "CLEAR_FILTERS" }) }
                    : { label: "Show all favorites", onClick: () => setActiveCollection("all") }
                }
              />
            )}
          </div>
        ) : (
          <EmptyState
            icon={Heart}
            title="No Favorites Yet"
            text="Tap the heart on any recipe and it will be saved here."
            action={{ label: "Back to Home", to: "/" }}
          />
        )}
      </main>

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          recipe={recipeData}
        />
      )}
    </>
  );
};

/* ---------- Tag Filter Component ---------- */
const TagFilter = ({ favorites, selected, onChange }) => {
  const allTags = new Set();
  favorites.forEach((r) => {
    [
      ...(r.cuisines || []),
      ...(r.dishTypes || []),
      ...(r.diets || []),
      ...(r.strCategory ? [r.strCategory] : []),
      ...(r.strArea ? [r.strArea] : []),
    ].forEach(
      (t) => allTags.add(t)
    );
  });
  const tags = Array.from(allTags).sort();

  const toggle = (tag) => {
    const newSel = selected.includes(tag)
      ? selected.filter((t) => t !== tag)
      : [...selected, tag];
    onChange(newSel);
  };

  if (tags.length === 0) return null;

  return (
    <div className="tag-filter">
      {tags.map((tag) => (
        <label key={tag} className="tag-chip">
          <input
            type="checkbox"
            checked={selected.includes(tag)}
            onChange={() => toggle(tag)}
          />
          {tag}
        </label>
      ))}
    </div>
  );
};

export default Favorites;
